import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';

export interface SuperAdminInfo {
  _id?: string;
  username?: string;
  name?: string;
  email?: string;
  role?: string;
}

const TOKEN_KEY = 'superAdminToken';
const INFO_KEY = 'superAdminInfo';

interface SuperAdminAuthState {
  token: string | null;
  info: SuperAdminInfo | null;
  isAuthenticated: boolean;
  hydrated: boolean;
  loginSuccess: (token: string, info?: SuperAdminInfo | null) => void;
  setInfo: (info: SuperAdminInfo | null) => void;
  logout: () => void;
  hydrate: () => void;
}

function readInfo(): SuperAdminInfo | null {
  const raw = localStorage.getItem(INFO_KEY);
  if (!raw) return null;
  try {
    return JSON.parse(raw) as SuperAdminInfo;
  } catch {
    return null;
  }
}

export const useSuperAdminAuthStore = create<SuperAdminAuthState>()(
  persist(
    (set) => ({
      token: null,
      info: null,
      isAuthenticated: false,
      hydrated: false,

      loginSuccess: (token, info) => {
        localStorage.setItem(TOKEN_KEY, token);
        if (info) localStorage.setItem(INFO_KEY, JSON.stringify(info));
        set({ token, info: info ?? null, isAuthenticated: true, hydrated: true });
      },

      setInfo: (info) => {
        if (info) localStorage.setItem(INFO_KEY, JSON.stringify(info));
        else localStorage.removeItem(INFO_KEY);
        set({ info });
      },

      logout: () => {
        localStorage.removeItem(TOKEN_KEY);
        localStorage.removeItem(INFO_KEY);
        set({ token: null, info: null, isAuthenticated: false });
      },

      hydrate: () => {
        const token = localStorage.getItem(TOKEN_KEY);
        set({
          token,
          info: readInfo(),
          isAuthenticated: !!token,
          hydrated: true,
        });
      },
    }),
    {
      name: 'timmy-superadmin-auth',
      storage: createJSONStorage(() => localStorage),
      partialize: (s) => ({ info: s.info }),
    },
  ),
);

export const superAdminTokenStorage = {
  get: () => localStorage.getItem(TOKEN_KEY),
  clear: () => {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(INFO_KEY);
  },
};
